/**
 * SEE-1200: structured logging for every SSO attempt (start/success/failure).
 *
 * Each attempt gets a correlation id that survives the round-trip through the
 * IdP: the browser (or the native shell's system-browser auth session) leaves
 * the page during the OAuth dance, so the attempt is persisted to
 * sessionStorage at start and resolved after the redirect lands back —
 * success in AuthProvider (a session exists with an attempt pending), failure
 * in the login screens (error params / Hub failure / a return to login with
 * no session).
 *
 * Events ship to Sentry via captureMessage with low-cardinality tags
 * (sso_event, sso_provider, sso_failure_code, sso_platform, sso_app_version)
 * so mobile SSO attempts are searchable and separable from web. The email is included in `extra`: it is business-necessary for SSO
 * triage (which account failed provisioning) and the SEE-1087 redaction layer
 * deliberately does not scrub emails.
 */
import { addBreadcrumb, captureMessage } from '@sentry/react';

/**
 * Substring that identifies the PreSignup/PostAuthentication "not provisioned"
 * rejection in Cognito's free-text error. Lives here (not ssoErrorSignal) so
 * importing the classifier never loads the Amplify Hub — ssoErrorSignal
 * registers a Hub listener at module load, which breaks any consumer whose
 * environment mocks aws-amplify without Hub.
 */
export const NOT_PROVISIONED_SIGNATURE = 'does not have access';

const STORAGE_KEY = 'speedchain.ssoAttempt';

/** Same UA contract the shell sets: SpeedchainMobile/<os>/<version>. */
const MOBILE_UA_RE = /SpeedchainMobile\/([^/]+)\/(\d+(?:\.\d+)*)/;

export type SsoFailureCode =
  | 'not_provisioned'
  | 'registration_required'
  | 'missing_profile_attributes'
  | 'invalid_saml'
  | 'cancelled'
  | 'redirect_failed'
  | 'returned_without_session'
  | 'native_sso_error'
  | 'other';

/**
 * SEE-1251: the auth gates append the identity the IdP actually authenticated
 * ("User does not have access to the application: a@b.com") so the error
 * screen can name the wrong account. Older deployments omit it — return
 * undefined then. Strips Cognito's trailing period.
 */
export function extractSsoIdentity(errorDescription: string): string | undefined {
  const match = errorDescription.match(/does not have access to the application[:\s]+([^\s]+@[^\s]+)/i);
  if (!match) return undefined;
  const identity = match[1].replace(/\.+$/, '').toLowerCase();
  return identity.includes('@') ? identity : undefined;
}

/**
 * SEE-1251: which sentence names the authenticated identity on a rejection
 * screen. Always name it when we know it — "the Microsoft account you used"
 * is useless to a user or to support without the address. The mismatch
 * wording only applies when we also know what they typed AND it differs;
 * a missing typed email (page restarted mid-flow, so the pending attempt is
 * gone) falls back to the plain line rather than "You entered , but…".
 * Shared so the web and mobile screens can't drift apart.
 */
export function ssoIdentityLineKey(
  typedEmail?: string,
  returnedIdentity?: string
): 'ssoIdentityMismatchLine' | 'ssoIdentityLine' | null {
  if (!returnedIdentity) return null;
  return typedEmail && typedEmail !== returnedIdentity ? 'ssoIdentityMismatchLine' : 'ssoIdentityLine';
}

/**
 * Failure code for a Cognito error_description. Single source of truth for
 * web Login and MobileSignIn — Cognito reports failures only as free text, so
 * both screens must recognize the same signatures.
 */
export function classifySsoError(errorDescription: string): SsoFailureCode {
  if (errorDescription.includes(NOT_PROVISIONED_SIGNATURE)) return 'not_provisioned';
  if (errorDescription.includes('REGISTRATION_REQUIRED:')) return 'registration_required';
  if (errorDescription.includes('givenName') || errorDescription.includes('familyName')) {
    return 'missing_profile_attributes';
  }
  if (errorDescription.includes('Invalid SAML')) return 'invalid_saml';
  return 'other';
}

export interface PendingSsoAttempt {
  correlationId: string;
  provider: string;
  email?: string;
  startedAt: number;
}

interface ResolveDetails {
  failureCode?: SsoFailureCode;
  /** Raw error detail (e.g. Cognito's error_description or a native code). */
  reason?: string;
}

function platformInfo(): { platform: string; appVersion: string } {
  const match = navigator.userAgent.match(MOBILE_UA_RE);
  return match ? { platform: match[1], appVersion: match[2] } : { platform: 'web', appVersion: 'web' };
}

function createCorrelationId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `sso-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function readPending(): PendingSsoAttempt | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingSsoAttempt;
    return parsed && typeof parsed.correlationId === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

function clearPending(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable — nothing to clear.
  }
}

function send(
  message: string,
  level: 'info' | 'warning',
  tags: Record<string, string>,
  extra: Record<string, unknown>
): void {
  const { platform, appVersion } = platformInfo();
  const allTags = { ...tags, sso_platform: platform, sso_app_version: appVersion };
  addBreadcrumb({ category: 'sso', message, level, data: { ...allTags, ...extra } });
  captureMessage(message, { level, tags: allTags, extra });
}

/**
 * Record the start of an SSO attempt, right before the redirect to the IdP.
 * Persists the attempt so the post-redirect page load can resolve it.
 */
export function startSsoAttempt({ provider, email }: { provider: string; email?: string }): string {
  const attempt: PendingSsoAttempt = {
    correlationId: createCorrelationId(),
    provider,
    email,
    startedAt: Date.now(),
  };
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attempt));
  } catch {
    // Storage unavailable: the attempt still logs, it just can't be correlated
    // with its outcome after the redirect.
  }
  send(
    'SSO attempt started',
    'info',
    { sso_event: 'start', sso_provider: provider },
    { correlationId: attempt.correlationId, email, timestamp: new Date(attempt.startedAt).toISOString() }
  );
  return attempt.correlationId;
}

export function getPendingSsoAttempt(): PendingSsoAttempt | null {
  return readPending();
}

/**
 * Resolve the pending attempt (clearing it). Failures without a pending
 * attempt still log — e.g. the page was restarted mid-flow and sessionStorage
 * was lost — just without correlation to their start event.
 */
export function resolveSsoAttempt(outcome: 'success' | 'failure', details: ResolveDetails = {}): void {
  const pending = readPending();
  clearPending();
  if (outcome === 'success' && !pending) return;

  const { failureCode, reason } = details;
  const tags: Record<string, string> = {
    sso_event: outcome,
    sso_provider: pending?.provider ?? 'unknown',
  };
  if (outcome === 'failure') tags.sso_failure_code = failureCode ?? 'other';

  send(
    outcome === 'success' ? 'SSO attempt succeeded' : 'SSO attempt failed',
    outcome === 'success' ? 'info' : 'warning',
    tags,
    {
      correlationId: pending?.correlationId ?? 'unknown',
      email: pending?.email,
      reason,
      durationMs: pending ? Date.now() - pending.startedAt : undefined,
      timestamp: new Date().toISOString(),
    }
  );
}
